'use client'

import { useId } from 'react'

import { cn } from '@/lib/utils'

/**
 * Decorative corner-glow SVG used behind the "gradient card" banners
 * (pending invites, venue status). Ids are namespaced per-instance via
 * `useId()` — the raw Figma export hardcodes its gradient/filter/clipPath
 * ids, which collide (and can silently swap which element a `url(#id)`
 * resolves to) the moment more than one card using it renders on the same
 * page at once.
 */
export function CardCornerGlow({ className }: { className?: string }) {
  const uid = useId()
  const filterId = `glow-filter-${uid}`
  const clipId = `glow-clip-${uid}`
  const paint0 = `glow-paint0-${uid}`
  const paint1 = `glow-paint1-${uid}`
  const paint2 = `glow-paint2-${uid}`
  const shadowResult = `glow-shadow-${uid}`

  return (
    <svg
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute top-1/2 right-4 -z-1 -translate-y-2/3',
        className
      )}
      width="221"
      height="71"
      viewBox="0 0 221 71"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <foreignObject x="-0.000725567" y="-52.2631" width="220.457" height="211.121">
        <div
          style={{
            backdropFilter: 'blur(1.22px)',
            clipPath: `url(#${clipId})`,
            height: '100%',
            width: '100%',
          }}
        />
      </foreignObject>
      <g filter={`url(#${filterId})`} data-figma-bg-blur-radius="2.43877">
        <path
          d="M112.617 56.8525V47.1475C112.617 36.8884 117.074 27.1352 124.83 20.4204L180.168 -27.4866C181.046 -28.2473 182.375 -28.1516 183.136 -27.273L193.603 -15.1818C194.364 -14.3032 194.268 -12.9743 193.39 -12.2137L138.052 35.693C134.728 38.5708 132.818 42.7508 132.818 47.1475V56.8525C132.818 61.2492 134.728 65.4292 138.052 68.307L193.39 116.214C194.268 116.974 194.364 118.303 193.603 119.182L183.136 131.273C182.375 132.152 181.046 132.247 180.168 131.487L124.83 83.5796C117.074 76.8648 112.617 67.1116 112.617 56.8525ZM85.683 56.901V47.099C85.683 42.7191 83.7875 38.5534 80.4852 35.6761L25.5032 -12.2299C24.627 -12.9934 24.5356 -14.3226 25.299 -15.1988L35.8049 -27.2561C36.5684 -28.1323 37.8976 -28.2236 38.7738 -27.4602L93.7555 20.4459C101.461 27.1596 105.884 36.8792 105.884 47.099V56.901C105.884 67.1208 101.461 76.8404 93.7555 83.5541L38.7738 131.46C37.8976 132.224 36.5684 132.132 35.8049 131.256L25.299 119.199C24.5356 118.323 24.627 116.993 25.5032 116.23L80.4852 68.3239C83.7875 65.4466 85.683 61.2809 85.683 56.901Z"
          fill={`url(#${paint0})`}
          fillOpacity="0.04"
          shapeRendering="crispEdges"
        />
        <path
          d="M112.617 56.8525V47.1475C112.617 36.8884 117.074 27.1352 124.83 20.4204L180.168 -27.4866C181.046 -28.2473 182.375 -28.1516 183.136 -27.273L193.603 -15.1818C194.364 -14.3032 194.268 -12.9743 193.39 -12.2137L138.052 35.693C134.728 38.5708 132.818 42.7508 132.818 47.1475V56.8525C132.818 61.2492 134.728 65.4292 138.052 68.307L193.39 116.214C194.268 116.974 194.364 118.303 193.603 119.182L183.136 131.273C182.375 132.152 181.046 132.247 180.168 131.487L124.83 83.5796C117.074 76.8648 112.617 67.1116 112.617 56.8525ZM85.683 56.901V47.099C85.683 42.7191 83.7875 38.5534 80.4852 35.6761L25.5032 -12.2299C24.627 -12.9934 24.5356 -14.3226 25.299 -15.1988L35.8049 -27.2561C36.5684 -28.1323 37.8976 -28.2236 38.7738 -27.4602L93.7555 20.4459C101.461 27.1596 105.884 36.8792 105.884 47.099V56.901C105.884 67.1208 101.461 76.8404 93.7555 83.5541L38.7738 131.46C37.8976 132.224 36.5684 132.132 35.8049 131.256L25.299 119.199C24.5356 118.323 24.627 116.993 25.5032 116.23L80.4852 68.3239C83.7875 65.4466 85.683 61.2809 85.683 56.901Z"
          stroke={`url(#${paint1})`}
          strokeOpacity="0.6"
          strokeWidth="0.215539"
          shapeRendering="crispEdges"
        />
        <path
          d="M112.617 56.8525V47.1475C112.617 36.8884 117.074 27.1352 124.83 20.4204L180.168 -27.4866C181.046 -28.2473 182.375 -28.1516 183.136 -27.273L193.603 -15.1818C194.364 -14.3032 194.268 -12.9743 193.39 -12.2137L138.052 35.693C134.728 38.5708 132.818 42.7508 132.818 47.1475V56.8525C132.818 61.2492 134.728 65.4292 138.052 68.307L193.39 116.214C194.268 116.974 194.364 118.303 193.603 119.182L183.136 131.273C182.375 132.152 181.046 132.247 180.168 131.487L124.83 83.5796C117.074 76.8648 112.617 67.1116 112.617 56.8525ZM85.683 56.901V47.099C85.683 42.7191 83.7875 38.5534 80.4852 35.6761L25.5032 -12.2299C24.627 -12.9934 24.5356 -14.3226 25.299 -15.1988L35.8049 -27.2561C36.5684 -28.1323 37.8976 -28.2236 38.7738 -27.4602L93.7555 20.4459C101.461 27.1596 105.884 36.8792 105.884 47.099V56.901C105.884 67.1208 101.461 76.8404 93.7555 83.5541L38.7738 131.46C37.8976 132.224 36.5684 132.132 35.8049 131.256L25.299 119.199C24.5356 118.323 24.627 116.993 25.5032 116.23L80.4852 68.3239C83.7875 65.4466 85.683 61.2809 85.683 56.901Z"
          stroke={`url(#${paint2})`}
          strokeOpacity="0.6"
          strokeWidth="0.215539"
          shapeRendering="crispEdges"
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="-0.000725567"
          y="-52.2631"
          width="220.457"
          height="211.121"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="24" operator="dilate" in="SourceAlpha" result={shadowResult} />
          <feOffset dx="0.778331" dy="1.29722" />
          <feGaussianBlur stdDeviation="0.726442" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.0185405 0 0 0 0 0.171941 0 0 0 0 0.225398 0 0 0 0.2 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result={shadowResult} />
          <feBlend mode="normal" in="SourceGraphic" in2={shadowResult} result="shape" />
        </filter>
        <clipPath id={clipId} transform="translate(0.000725567 52.2631)">
          <path d="M112.617 56.8525V47.1475C112.617 36.8884 117.074 27.1352 124.83 20.4204L180.168 -27.4866C181.046 -28.2473 182.375 -28.1516 183.136 -27.273L193.603 -15.1818C194.364 -14.3032 194.268 -12.9743 193.39 -12.2137L138.052 35.693C134.728 38.5708 132.818 42.7508 132.818 47.1475V56.8525C132.818 61.2492 134.728 65.4292 138.052 68.307L193.39 116.214C194.268 116.974 194.364 118.303 193.603 119.182L183.136 131.273C182.375 132.152 181.046 132.247 180.168 131.487L124.83 83.5796C117.074 76.8648 112.617 67.1116 112.617 56.8525ZM85.683 56.901V47.099C85.683 42.7191 83.7875 38.5534 80.4852 35.6761L25.5032 -12.2299C24.627 -12.9934 24.5356 -14.3226 25.299 -15.1988L35.8049 -27.2561C36.5684 -28.1323 37.8976 -28.2236 38.7738 -27.4602L93.7555 20.4459C101.461 27.1596 105.884 36.8792 105.884 47.099V56.901C105.884 67.1208 101.461 76.8404 93.7555 83.5541L38.7738 131.46C37.8976 132.224 36.5684 132.132 35.8049 131.256L25.299 119.199C24.5356 118.323 24.627 116.993 25.5032 116.23L80.4852 68.3239C83.7875 65.4466 85.683 61.2809 85.683 56.901Z" />
        </clipPath>
        <linearGradient
          id={paint0}
          x1="174.259"
          y1="96.6757"
          x2="66.3446"
          y2="-2.67973"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.21319" stopColor="#548799" stopOpacity="0.2" />
          <stop offset="1" stopColor="#8CE0FF" />
        </linearGradient>
        <linearGradient
          id={paint1}
          x1="234.206"
          y1="-15.949"
          x2="107.168"
          y2="61.8204"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.261287" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="0.444177" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#297D99" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={paint2}
          x1="22.8027"
          y1="46.4646"
          x2="109.438"
          y2="91.4312"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="0.397988" stopColor="white" stopOpacity="0.0334153" />
          <stop offset="0.903844" stopColor="white" stopOpacity="0.8" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  )
}
